import { Link } from "react-router-dom";
import { ArrowUpRight, Linkedin } from "lucide-react";
import Seo from "@/components/site/Seo";
import JsonLd from "@/components/site/JsonLd";
import DataFlowGraphic from "@/components/site/DataFlowGraphic";
import BookCall from "@/components/site/BookCall";

const FOUNDER_PHOTO = "https://customer-assets.emergentagent.com/job_xadentra-pro/artifacts/sa9jya8h_20240528_marci_25329_r2.webp";
const FOUNDER_LINKEDIN = "https://www.linkedin.com/in/marton-david-ivanyi/";

const FOUNDER_HIGHLIGHTS = [
  { k: "14+", v: "Years at Unilever" },
  { k: "#1", v: "Data & AI adoption in Europe" },
  { k: "P&L", v: "Owned across multiple markets" },
  { k: "EE", v: "Data & Analytics Market Lead" },
];

const VALUES = [
  { t: "Senior operators only", b: "You work with the people doing the work, not an account manager who hands off to a junior pool." },
  { t: "Write it down", b: "Lifecycle definitions, routing logic, QA checklists, sync rules — documented so the work survives turnover." },
  { t: "Boring details first", b: "Naming, channels, QA, change logs. Pipeline reliability lives in the parts nobody wants to own." },
  { t: "Honest scope", b: "If it's the wrong fix, we say so. Sometimes a working session is more useful than an engagement." },
  { t: "Shadow mode where it counts", b: "Sync changes, routing rules, lifecycle redesign — shipped read-only first so we can prove parity before cutover." },
  { t: "Built to be inherited", b: "Documented, named consistently, and handed back to your in-house team without a vendor lock-in." },
];

export default function About() {
  return (
    <>
      <Seo
        title="About Xadentra | Marketing automation specialists"
        description="Xadentra is a B2B marketing operations agency built by senior Adobe Marketo Engage and Salesforce operators. We help marketing teams build scalable, measurable, reliable systems."
        path="/about"
      />
      <JsonLd
        id="about-breadcrumb"
        data={{
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "Home", item: "https://xadentra.com/" },
            { "@type": "ListItem", position: 2, name: "About", item: "https://xadentra.com/about" },
          ],
        }}
      />
      <JsonLd
        id="founder-person"
        data={{
          "@context": "https://schema.org",
          "@type": "Person",
          name: "Márton Dávid Iványi",
          jobTitle: "Founder & CEO",
          worksFor: { "@type": "Organization", name: "Xadentra", url: "https://xadentra.com/" },
          url: "https://xadentra.com/about",
          image: FOUNDER_PHOTO,
          sameAs: [FOUNDER_LINKEDIN],
          description:
            "Founder & CEO of Xadentra. 14+ years at Unilever leading digital transformation, data strategy, and operational excellence across Eastern Europe.",
        }}
      />

      <section className="bg-[#07111F] text-white relative overflow-hidden">
        <div className="xa-blob" style={{ width: 480, height: 480, background: "#6D4DFF", top: -180, right: -120, opacity: 0.5 }} />
        <div className="xa-blob" style={{ width: 360, height: 360, background: "#00D4FF", bottom: -120, left: -80, opacity: 0.35 }} />
        <div className="absolute inset-0 xa-grid-dark opacity-50 pointer-events-none" />
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-24 lg:py-32 relative grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-7">
            <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#00D4FF]">About</div>
            <h1 className="font-display mt-6 text-4xl sm:text-5xl lg:text-6xl xl:text-7xl font-bold tracking-tight leading-[1.1] sm:leading-[1.05] lg:leading-[0.98]">
              A specialist B2B marketing operations agency.
            </h1>
            <p className="mt-8 text-lg text-white/70 leading-relaxed max-w-2xl">
              Xadentra is a new agency founded by senior practitioners who&apos;ve spent years
              inside enterprise marketing teams and other agencies — running Marketo
              instances, hardening Salesforce sync, designing lifecycle stages, and fixing
              the reporting that quietly stopped making sense.
            </p>
            <p className="mt-6 text-lg text-white/70 leading-relaxed max-w-2xl">
              We started Xadentra to do this work the way we always wished it was done.
              Senior operators on every engagement, focused on{" "}
              <strong className="text-white">Adobe Marketo Engage</strong> and{" "}
              <strong className="text-white">Salesforce</strong>, and the lifecycle, routing,
              and reporting work that connects campaigns to pipeline.
            </p>
            <p className="mt-6 text-sm text-white/55 leading-relaxed max-w-2xl">
              Because we&apos;re a new brand, we lean on the work itself. Every engagement
              starts with a scoping call and a written assessment so you can decide before
              you commit.
            </p>
          </div>
          <div className="lg:col-span-5">
            <div className="xa-card-dark rounded-2xl p-4 lg:p-6">
              <DataFlowGraphic className="w-full h-auto" />
            </div>
          </div>
        </div>
      </section>

      <section className="bg-[#0B1728] text-white relative overflow-hidden border-t border-white/5">
        <div className="xa-blob pointer-events-none" style={{ width: 520, height: 520, background: "#6D4DFF", top: -160, right: -160, opacity: 0.28 }} />
        <div className="xa-blob pointer-events-none" style={{ width: 380, height: 380, background: "#00D4FF", bottom: -140, left: -120, opacity: 0.18 }} />
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-24 lg:py-32 relative">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-start">
            <div className="lg:col-span-5">
              <div className="relative">
                <div
                  className="absolute -inset-3 rounded-[28px] opacity-40 blur-2xl"
                  style={{ background: "linear-gradient(135deg,#6D4DFF 0%,#00D4FF 100%)" }}
                  aria-hidden
                />
                <div className="relative rounded-[24px] overflow-hidden border border-white/10 bg-[#07111F]">
                  <img
                    src={FOUNDER_PHOTO}
                    alt="Márton Dávid Iványi, Founder and CEO of Xadentra"
                    width={960}
                    height={1280}
                    loading="lazy"
                    className="w-full h-auto object-cover"
                    data-testid="founder-photo"
                  />
                  <div className="pointer-events-none absolute inset-x-0 bottom-0 h-24 bg-gradient-to-t from-[#07111F] to-transparent" />
                  <div className="absolute left-5 bottom-5 flex items-center gap-2 text-xs font-mono uppercase tracking-[0.2em] text-[#00D4FF]">
                    <span className="h-1.5 w-1.5 rounded-full bg-[#00D4FF] shadow-[0_0_10px_#00D4FF]" />
                    Founder & CEO
                  </div>
                </div>
              </div>

              <div className="mt-6 grid grid-cols-2 gap-3">
                {FOUNDER_HIGHLIGHTS.map((h, i) => (
                  <div
                    key={i}
                    data-testid={`founder-highlight-${i}`}
                    className="rounded-xl border border-white/10 bg-white/[0.03] px-4 py-3"
                  >
                    <div className="font-display text-2xl font-bold text-white leading-none">{h.k}</div>
                    <div className="mt-1 text-[11px] uppercase tracking-[0.14em] text-white/55">{h.v}</div>
                  </div>
                ))}
              </div>
            </div>

            <div className="lg:col-span-7">
              <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#00D4FF]">From the Founder</div>
              <h2
                className="font-display mt-6 text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.05]"
                data-testid="founder-name"
              >
                Márton Dávid Iványi
              </h2>
              <div className="mt-3 text-white/60 text-sm font-mono uppercase tracking-[0.18em]">
                Founder & CEO, Xadentra
              </div>

              <div className="mt-8 space-y-5 text-white/75 leading-relaxed text-[15px] lg:text-base max-w-2xl">
                <p>
                  I founded Xadentra to fix the operational bottlenecks, broken system syncs,
                  and messy lifecycle stages that quietly slow down B2B pipelines. Marketing
                  automation should compound — most teams are stuck patching it.
                </p>
                <p>
                  Before Xadentra, I spent over <strong className="text-white">14 years at Unilever</strong>,
                  driving digital transformation, data strategy, and operational excellence
                  across Eastern Europe. As <strong className="text-white">Data & Analytics Market Lead</strong>,
                  I delivered AI initiatives with the highest adoption rate in Europe. As a
                  Finance Business Partner, I owned full P&Ls, led strategic pricing, and
                  drove measurable profitability across multiple markets.
                </p>
                <p>
                  Generating revenue takes more than creative ideas — it demands scalable
                  operations, seamless data integration, and rigorous project management. At
                  Xadentra, I bridge the gap between complex business needs and technical
                  delivery. While my expert co-founders own the deep technical architecture,
                  system integrations, and campaign strategy across Marketo and Salesforce,
                  I focus on client outcomes, business administration, and making sure every
                  engagement aligns with your core objectives.
                </p>
                <p className="text-white/55">
                  Outside of Xadentra, I&apos;m a public speaker, a former championship-winning
                  American football coach, and a volunteer firefighter. I believe in
                  high-performing teams, leading with resilience, and unlocking real value
                  through digital enablement.
                </p>
              </div>

              <div className="mt-10 flex flex-wrap items-center gap-3">
                <a
                  href={FOUNDER_LINKEDIN}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="founder-linkedin-btn"
                  className="group inline-flex items-center gap-2 rounded-full bg-white text-[#07111F] px-5 py-3 text-sm font-semibold hover:bg-[#00D4FF] transition-colors"
                >
                  <Linkedin className="h-4 w-4" />
                  Connect on LinkedIn
                  <ArrowUpRight className="h-4 w-4 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
                </a>
                <Link
                  to="/contact"
                  data-testid="founder-contact-btn"
                  className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/[0.03] px-5 py-3 text-sm font-semibold text-white hover:bg-white/[0.08] transition-colors"
                >
                  Talk to Márton
                  <ArrowUpRight className="h-4 w-4" />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-[#F7F9FC] border-b border-[#E5E7EB]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-24">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
            <div className="lg:col-span-4">
              <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#6D4DFF]">Operating values</div>
              <h2 className="font-display mt-4 text-4xl lg:text-5xl font-bold tracking-tight text-[#07111F]">
                Six rules. Non-negotiable.
              </h2>
            </div>
            <div className="lg:col-span-8 grid grid-cols-1 md:grid-cols-2 gap-4">
              {VALUES.map((v, i) => (
                <div key={i} data-testid={`about-value-${i}`} className="bg-white border border-[#E5E7EB] rounded-xl p-7">
                  <div className="font-mono text-xs text-[#6D4DFF]">{String(i + 1).padStart(2, "0")}</div>
                  <h3 className="font-display mt-3 text-xl font-semibold text-[#07111F]">{v.t}</h3>
                  <p className="mt-3 text-sm text-[#6B7280] leading-relaxed">{v.b}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="bg-[#07111F] text-white relative overflow-hidden">
        <div className="xa-blob pointer-events-none" style={{ width: 460, height: 460, background: "#6D4DFF", top: -160, left: -120, opacity: 0.4 }} />
        <div className="xa-blob pointer-events-none" style={{ width: 380, height: 380, background: "#00D4FF", bottom: -140, right: -100, opacity: 0.25 }} />
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-24 grid grid-cols-1 lg:grid-cols-12 gap-10 items-end relative">
          <div className="lg:col-span-8">
            <h2 className="font-display text-4xl lg:text-5xl font-bold tracking-tight">
              Work with Xadentra.
            </h2>
            <p className="mt-5 text-white/70 max-w-2xl">
              Tell us what you&apos;re trying to improve. A senior strategist will reply within one
              business day.
            </p>
          </div>
          <BookCall source="about_cta" className="lg:col-span-4 lg:justify-self-end w-fit">
            Book a meeting
          </BookCall>
        </div>
      </section>
    </>
  );
}
